import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Box, Button, Chip, Collapse, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import toast from 'react-hot-toast';
import { classObservationsAPI } from '../../services/api';

const OUTCOMES = [
  { value: 'all_good', label: 'All good' },
  { value: 'needs_attention', label: 'Needs attention' },
  { value: 'issue_found', label: 'Issue found' },
  { value: 'follow_up', label: 'Follow up' },
];

const SENTIMENTS = [
  { value: 'positive', label: 'Positive' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'negative', label: 'Negative' },
];

function teacherLine(label, name) {
  return `${label}: ${name || 'Not assigned'}`;
}

function outcomeLabel(value) {
  return OUTCOMES.find((item) => item.value === value)?.label || value;
}

function sentimentLabel(value) {
  return SENTIMENTS.find((item) => item.value === value)?.label || value;
}

export default function ClassObservationCard({ sessionId }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [sentiment, setSentiment] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');

  const detailQuery = useQuery(['class-observation', sessionId], async () => {
    const res = await classObservationsAPI.getSession(sessionId);
    return res.data?.data;
  }, { enabled: Boolean(sessionId) && open });

  const saveMutation = useMutation(
    (payload) => classObservationsAPI.add(sessionId, payload),
    {
      onSuccess: () => {
        toast.success('Class observation saved');
        setOutcome('');
        setNotes('');
        setSentiment('');
        setNextFollowUpDate('');
        queryClient.invalidateQueries(['class-observation', sessionId]);
        queryClient.invalidateQueries(['class-observations']);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Could not save the observation'),
    }
  );

  const session = detailQuery.data?.session;
  const rows = detailQuery.data?.observations || [];

  return (
    <Paper variant="outlined" sx={{ mt: 1.5 }}>
      <Button
        fullWidth
        onClick={() => setOpen((value) => !value)}
        endIcon={(
          <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        )}
        sx={{ justifyContent: 'space-between', px: 2, py: 1.25, textTransform: 'none', color: 'text.primary' }}
      >
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="subtitle1" fontWeight={700}>Class observation</Typography>
          <Typography variant="body2" color="text.secondary">
            Add feedback from attending this class
          </Typography>
        </Box>
      </Button>
      <Collapse in={open}>
      <Box sx={{ px: 2, pb: 2 }}>
      {session ? (
        <Stack spacing={0.25} sx={{ mb: 1.5 }}>
          <Typography variant="body2">{session.batchName} · {session.sessionDate} {session.startTime ? `· ${session.startTime}` : ''}</Typography>
          <Typography variant="body2">{teacherLine('Primary', session.primaryTeacherName)}</Typography>
          <Typography variant="body2">{teacherLine('Secondary', session.secondaryTeacherName)}</Typography>
          <Typography variant="body2">{teacherLine('Substitute', session.substituteTeacherName)}</Typography>
        </Stack>
      ) : null}
      {detailQuery.isError ? (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {detailQuery.error?.response?.data?.message || 'Could not load this class observation.'}
        </Typography>
      ) : null}
      <Stack spacing={1.5}>
        <TextField select label="Outcome (optional)" value={outcome} onChange={(event) => setOutcome(event.target.value)}>
          <MenuItem value="">Not set</MenuItem>
          {OUTCOMES.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Observation notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          multiline
          minRows={3}
          required
        />
        <TextField select label="Sentiment (optional)" value={sentiment} onChange={(event) => setSentiment(event.target.value)}>
          <MenuItem value="">Not set</MenuItem>
          {SENTIMENTS.map((item) => (
            <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Follow-up date (optional)"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={nextFollowUpDate}
          onChange={(event) => setNextFollowUpDate(event.target.value)}
        />
        <Button
          variant="contained"
          disabled={!notes.trim() || saveMutation.isLoading}
          onClick={() => saveMutation.mutate({
            outcome: outcome || null,
            notes: notes.trim(),
            sentiment: sentiment || null,
            nextFollowUpDate: nextFollowUpDate || null,
          })}
        >
          Save observation
        </Button>
      </Stack>
      <Stack spacing={1.25} sx={{ mt: 2 }}>
        {rows.map((row) => (
          <Paper key={row.id} variant="outlined" sx={{ p: 1.25 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              {row.outcome ? <Chip size="small" label={outcomeLabel(row.outcome)} /> : null}
              {row.sentiment ? <Chip size="small" variant="outlined" label={sentimentLabel(row.sentiment)} /> : null}
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{row.notes}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
              {row.createdByName ? ` · ${row.createdByName}` : ''}
              {row.nextFollowUpDate ? ` · Follow-up ${row.nextFollowUpDate}` : ''}
            </Typography>
          </Paper>
        ))}
        {!detailQuery.isLoading && rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No observations for this class yet.</Typography>
        ) : null}
      </Stack>
      </Box>
      </Collapse>
    </Paper>
  );
}
